import React, { useState, useEffect, useCallback } from "react";
import { subscribeMediaQuery } from "../utils/mediaQuery";
import { isMetaInAppBrowser } from "../utils/inAppBrowser";
import "./OrientationGuard.css";

/** Altura mínima del viewport (px). Por debajo mostramos el aviso. */
const MIN_VIEWPORT_HEIGHT = 420;

/**
 * Por encima de esta altura en landscape consideramos que es desktop/tablet
 * con espacio suficiente; no mostramos el aviso de orientación.
 */
const LANDSCAPE_OK_HEIGHT = 600;

/**
 * Detecta si la orientación es horizontal (landscape) en un viewport pequeño,
 * o si el height del viewport es demasiado pequeño. En desktop/tablet con
 * altura suficiente no muestra el aviso.
 * Estilo cute/kawaii alineado con SinConexionKawaii y fallbacks de la app.
 */
export default function OrientationGuard() {
	const [showWarning, setShowWarning] = useState(false);

	const checkViewport = useCallback(() => {
		if (typeof window === "undefined") return;
		if (isMetaInAppBrowser()) {
			setShowWarning(false);
			return;
		}

		const width = window.innerWidth;
		const height =
			window.innerHeight ||
			(window.visualViewport && window.visualViewport.height) ||
			0;

		// En Instagram/WebView el height a veces llega 0 o mentira; no tapar la página.
		if (height < 80) {
			setShowWarning(false);
			return;
		}

		const isLandscape = width > height;
		const isHeightTooSmall = height < MIN_VIEWPORT_HEIGHT;
		const isLandscapeButOk = isLandscape && height >= LANDSCAPE_OK_HEIGHT;

		setShowWarning(
			isHeightTooSmall || (isLandscape && !isLandscapeButOk)
		);
	}, []);

	useEffect(() => {
		checkViewport();

		window.addEventListener("resize", checkViewport);
		window.addEventListener("orientationchange", checkViewport);

		const mediaQuery = window.matchMedia("(orientation: landscape)");
		const unsubscribeMedia = subscribeMediaQuery(mediaQuery, checkViewport);

		return () => {
			window.removeEventListener("resize", checkViewport);
			window.removeEventListener("orientationchange", checkViewport);
			unsubscribeMedia();
		};
	}, [checkViewport]);

	// Bloquear scroll del body cuando el aviso está visible
	useEffect(() => {
		if (showWarning) {
			const prev = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			return () => {
				document.body.style.overflow = prev;
			};
		}
	}, [showWarning]);

	if (!showWarning) return null;

	return (
		<div className="orientation-guard" role="alert" aria-live="polite">
			<div className="orientation-guard__content">
				{/* Círculos decorativos de fondo (kawaii) */}
				<span className="orientation-guard__blob orientation-guard__blob--1" aria-hidden="true" />
				<span className="orientation-guard__blob orientation-guard__blob--2" aria-hidden="true" />
				<span className="orientation-guard__blob orientation-guard__blob--3" aria-hidden="true" />

				{/* Ilustración kawaii: móvil en vertical + flecha de girar */}
				<div className="orientation-guard__icon" aria-hidden="true">
					<svg
						viewBox="0 0 120 120"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						{/* Flecha circular "gira" */}
						<g className="orientation-guard__arrow">
							<path
								d="M 95 60 A 35 35 0 1 1 25 60"
								stroke="#A78BFA"
								strokeWidth="4"
								strokeLinecap="round"
								fill="none"
							/>
							<path
								d="M 25 60 L 32 53 M 25 60 L 32 67"
								stroke="#A78BFA"
								strokeWidth="4"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</g>
						{/* Móvil en vertical */}
						<rect
							x="42"
							y="28"
							width="36"
							height="64"
							rx="6"
							ry="6"
							fill="#F8FAFC"
							stroke="#6366F1"
							strokeWidth="2.5"
						/>
						<rect
							x="52"
							y="38"
							width="16"
							height="24"
							rx="2"
							fill="#E0E7FF"
						/>
						{/* Ojos kawaii */}
						<circle cx="54" cy="72" r="2.5" fill="#1e1b4b" />
						<circle cx="66" cy="72" r="2.5" fill="#1e1b4b" />
						{/* Sonrisa */}
						<path
							d="M 54 80 Q 60 86 66 80"
							stroke="#1e1b4b"
							strokeWidth="2"
							strokeLinecap="round"
							fill="none"
						/>
					</svg>
				</div>

				<h2 className="orientation-guard__title">
					¡Mejor en vertical!
				</h2>
				<p className="orientation-guard__text">
					Gira tu dispositivo o amplía la ventana para una experiencia más bonita.
				</p>
			</div>
		</div>
	);
}
